import {
  FC,
  useContext,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
} from "react";
import { useModalAction } from "../../../components/modal-views/context";
import { useMutation } from "react-query";
import client from "../../../api";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertChannelNameFee,
  convertServiceName,
  getErrorMessage,
  getServiceName,
  getStatusStyle,
  logger,
  makeNumberWithFloatingDigits,
} from "../../../lib/utils";
import { convertPaymentSystem } from "../../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Form, Input, InputNumber, Table } from "antd";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
} from "src/lib/constants";
import {
  DailyLimit,
  Fee,
  Limitation,
  PaginationInput,
  ParamsSort,
  User,
} from "src/api/types";
import SearchUserIDBox from "src/components/shared/search-box";
import {
  DailyLimitDataType,
  FeeDataType,
  LimitationDataType,
  UserDataType,
} from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import { LockIcon } from "src/components/icons/lock-icon";
import { UnlockIcon } from "src/components/icons/unlock-icon";
import Button from "src/components/ui/button";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import UsersContainerPage from "src/components/users";
import { EditIcon } from "src/components/icons/edit-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import { CheckIcon } from "src/components/icons/check-icon";
import CurrencyDrop from "src/components/ui/dropdown-list";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: FeeDataType;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

type UserLimitationsPageProps = {
  id: number;
  user: User;
};

const BillingsDailyLimitationsPage: FC<UserLimitationsPageProps> = ({
  id,
  user,
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record: DailyLimitDataType) => record.key === editingKey;
  const { openModal, closeModal } = useModalAction();
  const [total, setTotal] = useState(0);

  const [dataSource, setDataSource] = useState<DailyLimitDataType[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [updatedData, setUpdatedData] = useState<DailyLimitDataType | null>(
    null
  );
  const [searchParams, setSearchParams] = useState<ParamsSort>({
    asset_code: "",
    channel_name: "",
    current_page: 1,
    per_page: LIST_PAGE_SIZE,
    service: "",
  });
  const [dataCurrency, setDataCurrency] = useState<string[]>([]);
  const [error, setError] = useState<any>();
  const [services, setServices] = useState<any>(["All"]);
  const [catData, setCatData] = useState<any>([]);
  const [categories, setCategories] = useState<any>([]);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 640 ? true : false);
    setIsSmall(window.innerWidth < 480 ? true : false);
  }
  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const handleWalletSelect = (value: string, type?: string): void => {
    if (type === "category") {
      if (value === "All") {
        setSearchParams({ ...searchParams, category: "" });
      } else {
        setSearchParams({ ...searchParams, category: value });
      }
      if (error?.category) {
        setError((prev: any) => ({
          ...prev,
          category: undefined,
        }));
      }
    } else if (type === "currency") {
      if (value === "All") {
        setSearchParams({ ...searchParams, asset_code: "" });
      } else {
        setSearchParams({ ...searchParams, asset_code: value });
      }
      if (error?.asset_code) {
        setError((prev: any) => ({
          ...prev,
          asset_code: undefined,
        }));
      }
    } else if (type === "service") {
      if (value === "All") {
        setSearchParams({ ...searchParams, service: "" });
      } else {
        setSearchParams({ ...searchParams, service: value });
      }
      if (error?.service) {
        setError((prev: any) => ({
          ...prev,
          service: undefined,
        }));
      }
    }
  };

  const { mutate: getList, isLoading } = useMutation(
    user.is_business
      ? client.users.getDailyBuisnessLimitations
      : client.users.getDailyIndividualLimitations,
    {
      onSuccess: (data: any) => {
        setTotal(data.total);
        const sortData = data.data.sort(
          (a: any, b: any) => a.asset_code - b.asset_code
        );
        return setDataSource(
          sortData.map((item: any) => ({
            key: item.id,
            ...item,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );
 
  const { mutate: getFeesList } = useMutation(client.fee.listFee, {
    onSuccess: (data) => {
      setDataCurrency(["All", ...data]);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: getCategoriesList } = useMutation(client.categories.all, {
    onSuccess: (data) => {
      setCatData(data);
      setCategories(["All", ...data.map((item: any) => item.cat_name[2])]);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });
  const editOnMobile = (key: number) => {
    const selData = dataSource.filter((item) => item.key === key)[0];
    openModal("UPDATE_DAILY_LIMITATIONS_VIEW", {
      ...selData,
      callback: onCallBack,
    });
  };

  const onCallBack = (data: DailyLimit) => {
    logger.log(data);
    setEditingKey(String(data.id));
    setUpdatedData({ key: data.id, ...data });

    setTimeout(() => {
      updateLimitations({ input: data, userId: id });
    }, 100);
  };

  const { mutate: updateLimitations, isLoading: isLoadingUpdate } = useMutation(
    user.is_business
      ? client.users.updateDailyBuisnessLimitation
      : client.users.updateDailyIndividualLimitation,
    {
      onSuccess: (data) => {
        logger.log(data);
        if (updatedData) {
          const newData = [...dataSource];
          const index = newData.findIndex((item) => editingKey == item.key);
          if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
              ...item,
              ...updatedData!,
            });
            setDataSource(newData);
            setEditingKey("");
          } else {
            newData.push(updatedData!);
            setDataSource(newData);
            setEditingKey("");
          }
        } else {
          setEditingKey("");
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  useEffect(() => {
    if (searchParams?.category !== "All") {
      const filterData = catData.filter(
        (item: any) => item.cat_name[2] === searchParams?.category
      );
      logger.log(filterData, "FILER");
      if (filterData.length > 0 && filterData[0].services?.length > 0) {
        setServices([
          "All",
          ...filterData[0].services?.map((item: any) => item.service_name[2]),
        ]);
      } else {
        setServices(["All"]);
      }
    }
  }, [categories, searchParams]);

  useEffect(() => {
    setSearchParams({ ...searchParams, service: "" });
  }, [searchParams.category]);
  
  useEffect(() => {
    isMounted &&
      getList({
        id,
        type: "money-mover",
        asset_code: searchParams.asset_code,
        channel_name: convertServiceName(searchParams.service || ""),
        per_page: searchParams.per_page,
        current_page: searchParams.current_page,
      });
    getFeesList();
    getCategoriesList();
  }, [
    isMounted,
    id,
    searchParams.current_page,
    searchParams.asset_code,
    searchParams.channel_name,
    searchParams.service,
  ]);

  useEffect(() => {
    isMounted &&
    getFeesList();
    getCategoriesList();
  }, [
    isMounted,
    id
  ]);

  const edit = (record: Partial<DailyLimitDataType> & { key: React.Key }) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key as string);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as DailyLimitDataType;
      logger.log(row);
      setUpdatedData(row);

      setTimeout(() => {
        updateLimitations({ input: { ...row, id: key as number }, userId: id });
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Currency name
        </p>
      ),
      dataIndex: "asset_code",
      key: "asset_code",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value?.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Channel Name
        </p>
      ),
      dataIndex: "channel_name",
      key: "channel_name",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {convertChannelNameFee(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Deposit max
        </p>
      ),
      dataIndex: "deposit_max",
      key: "deposit_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Withdraw max
        </p>
      ),
      dataIndex: "withdraw_max",
      key: "withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Deposit max
        </p>
      ),
      dataIndex: "vip_deposit_max",
      key: "vip_deposit_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Withdraw max
        </p>
      ),
      dataIndex: "vip_withdraw_max",
      key: "vip_withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_: any, record: DailyLimitDataType) => {
        const editable = isEditing(record);
        return editable ? (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={cancel}
            >
              <CloseIcon className="w-[20px] h-[20px] text-secondary" />
            </button>
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => save(record.key)}
            >
              <CheckIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => edit(record)}
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DailyLimitDataType) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: "",
        editing: isEditing(record),
      }),
    };
  });

  const mobileColumns: ColumnsType<DailyLimitDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-4 items-start justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Currency
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData?.asset_code?.toUpperCase()}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Channel Name
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {convertChannelNameFee(selData?.channel_name || "")}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Deposit max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(String(selData.deposit_max), 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Withd.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
                </p>
              </div>
            </div>
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  VIP Dept.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(
                    String(selData.vip_deposit_max),
                    2
                  )}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  VIP With.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium invisible">
                  Edit
                </p>
                <a
                  className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium"
                  onClick={() => editOnMobile(record.key as number)}
                >
                  Edit
                </a>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const smallColumns: ColumnsType<DailyLimitDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Currency
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {selData?.asset_code?.toUpperCase()}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Channel Name
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {selData?.channel_name}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Dept.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(String(selData.deposit_max), 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Withd.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Dept.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(
                  String(selData.vip_deposit_max),
                  2
                )}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP With.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium invisible">
                Edit
              </p>
              <a
                className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium"
                onClick={() => editOnMobile(record.key as number)}
              >
                Edit
              </a>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-r-[12px] rounded-bl-[12px] w-full relative">
      <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
      <div className="grid gap-3 grid-cols-12">
          <div className="lg:col-span-3 md:col-span-4 sm:col-span-6 col-span-12">
            <div
              className="input table-filter__item "
              style={{ width: "100%" }}
            >
              <p className="input__name input__name--type2">Currency</p>
              <CurrencyDrop
                value={
                  searchParams?.asset_code === ""
                    ? "All"
                    : searchParams?.asset_code
                }
                options={dataCurrency ? [...dataCurrency] : []}
                onChange={handleWalletSelect}
                type="currency"
              />
              <p className="text-red-500">
                {error &&
                  error.asset_code &&
                  typeof error.asset_code === "string" &&
                  error.asset_code}
              </p>
            </div>
          </div>
          <div className="lg:col-span-3 md:col-span-4 sm:col-span-6 col-span-12">
            <div className="input table-filter__item" style={{ width: "100%" }}>
              <p className="input__name input__name--type2">Category</p>
              <CurrencyDrop
                value={
                  searchParams.category === "" || !searchParams.category
                    ? "All"
                    : searchParams.category
                }
                options={categories ? [...categories] : []}
                onChange={handleWalletSelect}
                type="category"
              />
              <p className="text-red-500">
                {error &&
                  error.category &&
                  typeof error.category === "string" &&
                  error.category}
              </p>
            </div>
          </div>
          <div className="lg:col-span-3 md:col-span-4 sm:col-span-6 col-span-12">
            <div className="input table-filter__item" style={{ width: "100%" }}>
              <p className="input__name input__name--type2">Service</p>
              <CurrencyDrop
                value={
                  searchParams.service === "" || !searchParams.service
                    ? "All"
                    : searchParams.service
                }
                options={services ? [...services] : []}
                onChange={handleWalletSelect}
                type="service"
              />
              <p className="text-red-500">
                {error &&
                  error.service &&
                  typeof error.service === "string" &&
                  error.service}
              </p>
            </div>
          </div>
        </div>
        {isMobile ? (
          <Table
            loading={isLoading || isLoadingUpdate}
            showSorterTooltip={false}
            columns={isSmall ? smallColumns : mobileColumns}
            dataSource={dataSource}
            // pagination={false}
            pagination={{
              total,
              current: searchParams.current_page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
          />
        ) : (
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              loading={isLoading || isLoadingUpdate}
              showSorterTooltip={false}
              columns={mergedColumns}
              dataSource={dataSource}
              // pagination={false}
              pagination={{
                total,
                current: searchParams.current_page,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
              rowClassName="editable-row"
            />
          </Form>
        )}
      </div>
    </div>
  );
};

export default BillingsDailyLimitationsPage;
